import * as React from 'react';
import { useCallback } from 'react';
import * as D3Shape from 'd3-shape';
import { max, min } from 'd3-array';
import type { ChartSliderPropsType, LineDataType } from './types';

const ChartSlider: React.FC<ChartSliderPropsType> = ( props ) => {
    const {
        xPos,
        type,
        labelsTooClose,
        yRange,
        xRange,
        timeFormatter,
        xScaleSets,
    } = props;

    const getXScaleSetFromXRange = useCallback( ( xRangeVal: number ) => {
        return xScaleSets.filter( ( xScaleSet ) => (
            xRangeVal >= min( xScaleSet.range() ) && xRangeVal <= max( xScaleSet.range() )
        ) )[0];
    }, [ xScaleSets ] );

    const isTooCloseToBoundary = useCallback( ( xPosVal: number, sliderType: string, sliderDateBoxWidth: number ) => {
        if ( sliderType === 'left' ) {
            return Math.abs( xRange[1] - xPosVal ) < sliderDateBoxWidth;
        } else {
            return Math.abs( xRange[0] - xPosVal ) < sliderDateBoxWidth;
        }
    }, [ xRange ] );

    const sliderBoxWidth = 5;
    const sliderBoxHeight = 16;

    const sliderDateBoxWidth = 60;
    const rangeSpan = ( max( yRange ) - min( yRange ) );

    const isTooCloseToBound = isTooCloseToBoundary( xPos, type, sliderDateBoxWidth );

    let yAnchorBoxes = ( rangeSpan - sliderBoxHeight ) * 0.5;
    let yAnchorText = rangeSpan * 0.5;
    const optionalOffset = ( sliderBoxHeight / 2 ) + 1;

    if ( labelsTooClose ) {
        const offset = ( optionalOffset * ( type === 'left' ? -1 : 1 ) );
        yAnchorBoxes = yAnchorBoxes + offset;
        yAnchorText = yAnchorText + offset;
    }

    const xAnchorSliderBox = type === 'left' ? xPos - sliderBoxWidth : xPos;
    let xAnchorSliderDateBox = ( type === 'left' ? xPos : xPos - sliderDateBoxWidth );

    if ( isTooCloseToBound ) {
        xAnchorSliderDateBox = ( type === 'left' ? xPos - sliderDateBoxWidth - sliderBoxWidth :
            xPos + sliderBoxWidth );
    }

    const line = D3Shape.line<LineDataType>()
        .x( ( d: LineDataType ) => d.x )
        .y( ( d: LineDataType ) => d.y );

    const handlerXPos = xAnchorSliderBox + ( sliderBoxWidth / 2 ) - ( type === 'right' ? 1.5 : 1 );

    return (
        <g className={ `slider slider-${ type }` }>
            <rect
                x={ xAnchorSliderDateBox }
                y={ yAnchorBoxes }
                width={ sliderDateBoxWidth }
                height={ sliderBoxHeight }
                className='cursor-pointer fill-[#f3f3f3]'
            />
            <text
                x={ xAnchorSliderDateBox + 5 }
                y={ yAnchorText + 3 }
                className='pointer-events-auto cursor-pointer font-[Arial] text-[10px]'
                style={ { alignmentBaseline: 'central' } }
            >
                {getXScaleSetFromXRange( xPos ) ?
                    timeFormatter( getXScaleSetFromXRange( xPos ).invert( xPos ) ) : null}
            </text>
            <rect
                x={ xAnchorSliderBox }
                y={ yAnchorBoxes }
                width={ sliderBoxWidth }
                height={ sliderBoxHeight }
                className='cursor-pointer fill-[#668272]'
            />
            <path
                className='sliderLine cursor-pointer fill-none stroke-[#668272] stroke-[1.5]'
                d={ line( [ { x: xPos, y: yRange[0] }, { x: xPos, y: yRange[1] } ] ) }
            />
            <g
                transform={ `translate(${ handlerXPos }, ${ yAnchorBoxes + 4.5 }) scale(.5)` }
                className='sliderHandle [&_circle]:fill-white'
            >
                <circle cx='2.5' cy='13.5' r='1.5'/>
                <circle cx='2.5' cy='7.5' r='1.5'/>
                <circle cx='2.5' cy='1.5' r='1.5'/>
            </g>
        </g>
    );
};

export { ChartSlider };
